// atlas/apps/web/src/pages/TaskersPage.tsx
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pbAuthedFetch } from '../authHeaders';

interface Subtask { label: string; done: boolean }
interface Task {
  id: string; title: string; description?: string | null;
  status: 'todo' | 'in_progress' | 'done'; priority: string;
  assignee: string | null; due_date: string | null; linked_project: string | null;
  subtasks: Subtask[]; created_at: string; updated_at: string;
}
interface Stats { total: number; open: number; overdue: number; due_this_week: number; done: number }

const TABS: { key: 'all' | Task['status']; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'done', label: 'Done' },
];

interface TaskForm {
  title: string; description: string; priority: string; assignee: string; due_date: string; linked_project: string; subtasks: Subtask[];
}
const EMPTY_FORM: TaskForm = { title: '', description: '', priority: 'medium', assignee: '', due_date: '', linked_project: '', subtasks: [] };

export function TaskersPage() {
  const [tab, setTab] = useState<'all' | Task['status']>('all');
  const [titleSortAsc, setTitleSortAsc] = useState<boolean | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TaskForm>(EMPTY_FORM);
  const [newSubtaskLabel, setNewSubtaskLabel] = useState('');
  const [aiError, setAiError] = useState<string | null>(null);

  const [sendTask, setSendTask] = useState<Task | null>(null);
  const [sendTab, setSendTab] = useState<'email' | 'teams' | 'servicenow'>('email');
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [teamsMessage, setTeamsMessage] = useState('');
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [teamsSent, setTeamsSent] = useState(false);
  const [ticketShortDescription, setTicketShortDescription] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketUrgency, setTicketUrgency] = useState('3');
  const [ticketError, setTicketError] = useState<string | null>(null);
  const [ticketResult, setTicketResult] = useState<{ number: string; url: string } | null>(null);
  const queryClient = useQueryClient();

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setNewSubtaskLabel('');
    setAiError(null);
    setDrawerOpen(true);
  };
  const openEdit = (t: Task) => {
    setEditingId(t.id);
    setForm({
      title: t.title, description: t.description || '', priority: t.priority || 'medium', assignee: t.assignee || '',
      due_date: t.due_date || '', linked_project: t.linked_project || '', subtasks: [...t.subtasks],
    });
    setNewSubtaskLabel('');
    setAiError(null);
    setDrawerOpen(true);
  };
  const addSubtask = () => {
    if (!newSubtaskLabel.trim()) return;
    setForm((f) => ({ ...f, subtasks: [...f.subtasks, { label: newSubtaskLabel.trim(), done: false }] }));
    setNewSubtaskLabel('');
  };
  const toggleSubtask = (idx: number) => {
    setForm((f) => ({ ...f, subtasks: f.subtasks.map((s, i) => (i === idx ? { ...s, done: !s.done } : s)) }));
  };
  const removeSubtask = (idx: number) => {
    setForm((f) => ({ ...f, subtasks: f.subtasks.filter((_, i) => i !== idx) }));
  };

  const openSend = (t: Task) => {
    setSendTask(t);
    setSendTab('email');
    setEmailTo('');
    setEmailSubject('');
    setEmailBody('');
    setEmailError(null);
    setEmailSent(false);
    setSelectedTeamId(null);
    setSelectedChannelId(null);
    setTeamsMessage('');
    setTeamsError(null);
    setTeamsSent(false);
    setTicketShortDescription('');
    setTicketDescription('');
    setTicketUrgency('3');
    setTicketError(null);
    setTicketResult(null);
  };
  const closeSend = () => setSendTask(null);

  const { data: statsData } = useQuery<{ ok: boolean; stats: Stats }>({
    queryKey: ['tasks-stats'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/tasks/stats');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data, isLoading, error } = useQuery<{ ok: boolean; tasks: Task[] }>({
    queryKey: ['tasks', tab],
    queryFn: async () => {
      const url = tab === 'all' ? '/api/tasks' : `/api/tasks?status=${tab}`;
      const res = await pbAuthedFetch(url);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  interface TeamChannel { id: string; name: string }
  interface TeamWithChannels { id: string; name: string; channels: TeamChannel[] }
  const { data: teamsData } = useQuery<{ ok: boolean; teams: TeamWithChannels[] }>({
    queryKey: ['teams-channels'],
    enabled: !!sendTask,
    retry: false,
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/integrations/teams/channels');
      return res.json();
    },
  });
  const teamsList = teamsData?.teams || [];
  const effectiveTeamId = selectedTeamId ?? teamsList[0]?.id ?? '';
  const activeTeam = teamsList.find((t) => t.id === effectiveTeamId);
  const effectiveChannelId = selectedChannelId ?? activeTeam?.channels?.[0]?.id ?? '';
  const effectiveTeamsMessage = teamsMessage || [sendTask?.title, sendTask?.description].filter(Boolean).join('\n\n');

  const createTask = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title, description: form.description || null, priority: form.priority, assignee: form.assignee || null,
          due_date: form.due_date || null, linked_project: form.linked_project || null, subtasks: form.subtasks,
        }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-stats'] });
      setDrawerOpen(false);
    },
  });

  const updateTask = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch(`/api/tasks/${editingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title, description: form.description, priority: form.priority, assignee: form.assignee || null,
          due_date: form.due_date || null, linked_project: form.linked_project || null, subtasks: form.subtasks,
        }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-stats'] });
      setDrawerOpen(false);
    },
  });

  const patchTask = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Task['status'] }) => {
      const res = await pbAuthedFetch(`/api/tasks/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-stats'] });
    },
  });

  const rewriteMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/ai/tasks/rewrite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: form.title, description: form.description }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'ai_upstream_error');
      return json as { text: string };
    },
    onSuccess: (data) => {
      setForm((f) => ({ ...f, description: data.text }));
      setAiError(null);
    },
    onError: () => setAiError('Could not rewrite the body — try again.'),
  });

  const breakdownMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/ai/tasks/breakdown', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: form.title, description: form.description }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'ai_upstream_error');
      return json as { subtasks: Subtask[] };
    },
    onSuccess: (data) => {
      setForm((f) => ({ ...f, subtasks: [...f.subtasks, ...data.subtasks] }));
      setAiError(null);
    },
    onError: () => setAiError('Could not break this down — try again.'),
  });

  const suggestMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/ai/tasks/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: form.title, description: form.description }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'ai_upstream_error');
      return json as { priority: string | null; due_date: string | null };
    },
    onSuccess: (data) => {
      setForm((f) => ({ ...f, priority: data.priority || f.priority, due_date: data.due_date || f.due_date }));
      setAiError(null);
    },
    onError: () => setAiError('Could not suggest a due date or priority — try again.'),
  });

  const draftEmailMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/ai/tasks/draft-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: sendTask?.title, description: sendTask?.description }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'ai_upstream_error');
      return json as { subject: string; body: string };
    },
    onSuccess: (data) => {
      setEmailSubject(data.subject);
      setEmailBody(data.body);
      setEmailError(null);
    },
    onError: () => setEmailError('Could not draft the email — try again.'),
  });

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/integrations/outlook/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: emailTo, subject: emailSubject, body: emailBody }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'send_failed');
      return json;
    },
    onSuccess: () => {
      setEmailError(null);
      setEmailSent(true);
    },
    onError: (err: Error) => {
      setEmailSent(false);
      setEmailError(err.message === 'send_failed' ? 'Could not send the email — try again.' : err.message);
    },
  });

  const sendTeamsMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/integrations/teams/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId: effectiveTeamId, channelId: effectiveChannelId, message: effectiveTeamsMessage }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'send_failed');
      return json;
    },
    onSuccess: () => {
      setTeamsError(null);
      setTeamsSent(true);
    },
    onError: (err: Error) => {
      setTeamsSent(false);
      setTeamsError(err.message === 'send_failed' ? 'Could not send to Teams — try again.' : err.message);
    },
  });

  const draftTicketMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/ai/tasks/draft-ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: sendTask?.title, description: sendTask?.description }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'ai_upstream_error');
      return json as { short_description: string; description: string; urgency: string };
    },
    onSuccess: (data) => {
      setTicketShortDescription(data.short_description);
      setTicketDescription(data.description);
      setTicketUrgency(data.urgency);
      setTicketError(null);
    },
    onError: () => setTicketError('Could not draft the ticket — try again.'),
  });

  const createTicketMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/integrations/servicenow/incident', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ short_description: ticketShortDescription, description: ticketDescription, urgency: ticketUrgency }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'create_failed');
      return json as { number: string; url: string };
    },
    onSuccess: async (data) => {
      setTicketError(null);
      setTicketResult({ number: data.number, url: data.url });
      if (sendTask) {
        await pbAuthedFetch(`/api/tasks/${sendTask.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ servicenow_incident_number: data.number, servicenow_incident_url: data.url }),
        });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }
    },
    onError: (err: Error) => {
      setTicketResult(null);
      setTicketError(err.message === 'create_failed' ? 'Could not create the ticket — try again.' : err.message);
    },
  });

  const handleSave = () => (editingId ? updateTask.mutate() : createTask.mutate());

  const stats = statsData?.stats;
  const tasks = data?.tasks || [];
  const sortedTasks = titleSortAsc == null
    ? tasks
    : [...tasks].sort((a, b) => {
        const cmp = a.title.localeCompare(b.title);
        return titleSortAsc ? cmp : -cmp;
      });
  const toggleTitleSort = () => setTitleSortAsc((prev) => (prev === true ? false : true));

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Work</p>
            <h1 className="pagehead__title">Taskers</h1>
            <div className="pagehead__facts">
              <span className="fact">Routed from inbox triage or created directly · owner, due date, linked project and subtasks travel with the tasker</span>
            </div>
          </div>
          <div className="box__head-actions">
            <button className="btn btn--primary" onClick={openCreate}>+ New tasker</button>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
            <div className="ind"><p className="ind__abbr">TOTAL</p><p className="ind__v">{stats.total}</p></div>
            <div className="ind"><p className="ind__abbr">OPEN</p><p className="ind__v">{stats.open}</p></div>
            <div className="ind"><p className="ind__abbr">OVERDUE</p><p className="ind__v" style={{ color: 'var(--risk)' }}>{stats.overdue}</p></div>
            <div className="ind"><p className="ind__abbr">DUE THIS WEEK</p><p className="ind__v" style={{ color: 'var(--warn)' }}>{stats.due_this_week}</p></div>
            <div className="ind"><p className="ind__abbr">DONE</p><p className="ind__v" style={{ color: 'var(--ok)' }}>{stats.done}</p></div>
          </div>
        )}

        <div className="tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              className="tab"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="box">
          <div className="box__body">
            {isLoading && <div className="page-loading" role="status" aria-live="polite">Loading taskers…</div>}
            {error && (
              <div className="state state--risk">
                <h3 className="state__title">Failed to load taskers</h3>
                <p className="state__body">Could not connect to the tasks API.</p>
              </div>
            )}
            {!isLoading && !error && tasks.length === 0 && <p style={{ color: 'var(--ink-45)' }}>No taskers in this view.</p>}
            {!isLoading && !error && tasks.length > 0 && (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>
                      <button
                        className="th"
                        onClick={toggleTitleSort}
                        aria-sort={titleSortAsc == null ? 'none' : titleSortAsc ? 'ascending' : 'descending'}
                      >
                        Title{titleSortAsc != null && (titleSortAsc ? ' ▲' : ' ▼')}
                      </button>
                    </th>
                    <th><button className="th th--static">Owner</button></th>
                    <th><button className="th th--static">Due</button></th>
                    <th><button className="th th--static">Linked project</button></th>
                    <th><button className="th th--static">Subtasks</button></th>
                    <th><button className="th th--static">Status</button></th>
                    <th><button className="th th--static">Actions</button></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTasks.map((t) => {
                    const doneSubtasks = t.subtasks.filter((s) => s.done).length;
                    return (
                      <tr key={t.id}>
                        <td className="td--name">
                          <button
                            onClick={() => openEdit(t)}
                            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left' }}
                          >
                            {t.title}
                          </button>
                        </td>
                        <td>{t.assignee || '—'}</td>
                        <td>{t.due_date || '—'}</td>
                        <td>{t.linked_project || '—'}</td>
                        <td>{t.subtasks.length > 0 ? `${doneSubtasks}/${t.subtasks.length}` : '—'}</td>
                        <td>
                          <select
                            value={t.status}
                            onChange={(e) => patchTask.mutate({ id: t.id, status: e.target.value as Task['status'] })}
                          >
                            <option value="todo">To Do</option>
                            <option value="in_progress">In Progress</option>
                            <option value="done">Done</option>
                          </select>
                        </td>
                        <td>
                          <button className="btn btn--sm" onClick={() => openSend(t)}>Send</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <>
          <div className="scrim is-on" onClick={() => setDrawerOpen(false)} />
          <div
            className="modal is-on"
            role="dialog"
            aria-label={editingId ? 'Edit tasker' : 'New tasker'}
            style={{ width: 'min(640px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 64px)' }}
          >
            <div className="modal__head">
              <h2 style={{ flex: 1 }}>{editingId ? 'Edit tasker' : 'New tasker'}</h2>
              <button className="ibtn" onClick={() => setDrawerOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className="modal__body">
              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label htmlFor="tasker-title">Title</label>
                <input id="tasker-title" type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label htmlFor="tasker-body">Body</label>
                <textarea
                  id="tasker-body"
                  rows={5}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--sp-2)', marginBottom: 'var(--sp-4)' }}>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={!form.description.trim() || rewriteMutation.isPending}
                  onClick={() => rewriteMutation.mutate()}
                >
                  {rewriteMutation.isPending ? 'Rewriting…' : 'Rewrite with AI'}
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={!form.description.trim() || breakdownMutation.isPending}
                  onClick={() => breakdownMutation.mutate()}
                >
                  {breakdownMutation.isPending ? 'Breaking down…' : 'Breakdown with AI'}
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={!form.description.trim() || suggestMutation.isPending}
                  onClick={() => suggestMutation.mutate()}
                >
                  {suggestMutation.isPending ? 'Suggesting…' : 'Suggest with AI'}
                </button>
              </div>
              {aiError && <p style={{ color: 'var(--risk)', marginBottom: 'var(--sp-4)' }}>{aiError}</p>}
              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label htmlFor="tasker-priority">Priority</label>
                <select id="tasker-priority" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label htmlFor="tasker-owner">Owner</label>
                <input id="tasker-owner" type="text" value={form.assignee} onChange={(e) => setForm((f) => ({ ...f, assignee: e.target.value }))} />
              </div>
              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label htmlFor="tasker-due">Due date</label>
                <input id="tasker-due" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} />
              </div>
              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label htmlFor="tasker-project">Linked project</label>
                <input id="tasker-project" type="text" value={form.linked_project} onChange={(e) => setForm((f) => ({ ...f, linked_project: e.target.value }))} />
              </div>
              <div className="field">
                <label>Subtasks</label>
                {form.subtasks.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                    <input type="checkbox" checked={s.done} onChange={() => toggleSubtask(i)} aria-label={`${s.label} done`} />
                    <span style={{ flex: 1 }}>{s.label}</span>
                    <button className="ibtn" onClick={() => removeSubtask(i)} aria-label={`Remove ${s.label}`}>✕</button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
                  <input
                    type="text"
                    aria-label="Add subtask"
                    placeholder="Add a subtask"
                    value={newSubtaskLabel}
                    onChange={(e) => setNewSubtaskLabel(e.target.value)}
                  />
                  <button className="btn btn--sm" onClick={addSubtask}>Add subtask</button>
                </div>
              </div>
            </div>
            <div className="modal__foot">
              <button
                className="btn btn--primary"
                disabled={!form.title.trim() || createTask.isPending || updateTask.isPending}
                onClick={handleSave}
              >
                {editingId
                  ? (updateTask.isPending ? 'Saving…' : 'Save')
                  : (createTask.isPending ? 'Creating…' : 'Create tasker')}
              </button>
            </div>
          </div>
        </>
      )}

      {sendTask && (
        <>
          <div className="scrim is-on" onClick={closeSend} />
          <div
            className="modal is-on"
            role="dialog"
            aria-label={`Send: ${sendTask.title}`}
            style={{ width: 'min(560px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 64px)' }}
          >
            <div className="modal__head">
              <h2 style={{ flex: 1 }}>{sendTask.title}</h2>
              <button className="ibtn" onClick={closeSend} aria-label="Close">✕</button>
            </div>
            <div className="tabs" role="tablist" style={{ padding: '0 18px', flex: 'none' }}>
              {(['email', 'teams', 'servicenow'] as const).map((key) => (
                <button
                  key={key}
                  className="tab"
                  role="tab"
                  aria-selected={sendTab === key}
                  onClick={() => setSendTab(key)}
                >
                  {key === 'email' ? 'Email' : key === 'teams' ? 'Teams' : 'ServiceNow'}
                </button>
              ))}
            </div>
            <div className="modal__body">
              {sendTab === 'email' && (
                <div className="field">
                  <button
                    className="btn btn--ghost btn--sm"
                    style={{ marginBottom: 'var(--sp-2)' }}
                    disabled={!sendTask.description?.trim() || draftEmailMutation.isPending}
                    onClick={() => draftEmailMutation.mutate()}
                  >
                    {draftEmailMutation.isPending ? 'Drafting…' : 'Draft email with AI'}
                  </button>
                  <label htmlFor="tasker-email-to">To</label>
                  <input id="tasker-email-to" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} />
                  <label htmlFor="tasker-email-subject">Email subject</label>
                  <input id="tasker-email-subject" type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                  <label htmlFor="tasker-email-body">Email body</label>
                  <textarea id="tasker-email-body" rows={6} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
                  {emailError && <p style={{ color: 'var(--risk)' }}>{emailError}</p>}
                  {emailSent && <p style={{ color: 'var(--ok)' }}>Email sent.</p>}
                  <button
                    className="btn btn--sm"
                    style={{ marginTop: 'var(--sp-2)' }}
                    disabled={!emailTo.trim() || !emailSubject.trim() || !emailBody.trim() || sendEmailMutation.isPending}
                    onClick={() => sendEmailMutation.mutate()}
                  >
                    {sendEmailMutation.isPending ? 'Sending…' : 'Send email'}
                  </button>
                </div>
              )}

              {sendTab === 'teams' && (
                <div className="field">
                  <label htmlFor="tasker-teams-team">Team</label>
                  <select
                    id="tasker-teams-team"
                    value={effectiveTeamId}
                    onChange={(e) => {
                      setSelectedTeamId(e.target.value);
                      setSelectedChannelId(null);
                    }}
                  >
                    {teamsList.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <label htmlFor="tasker-teams-channel">Channel</label>
                  <select id="tasker-teams-channel" value={effectiveChannelId} onChange={(e) => setSelectedChannelId(e.target.value)}>
                    {(activeTeam?.channels || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <label htmlFor="tasker-teams-message">Message</label>
                  <textarea
                    id="tasker-teams-message"
                    rows={6}
                    value={effectiveTeamsMessage}
                    onChange={(e) => setTeamsMessage(e.target.value)}
                  />
                  {teamsError && <p style={{ color: 'var(--risk)' }}>{teamsError}</p>}
                  {teamsSent && <p style={{ color: 'var(--ok)' }}>Sent to Teams.</p>}
                  <button
                    className="btn btn--sm"
                    style={{ marginTop: 'var(--sp-2)' }}
                    disabled={!effectiveTeamId || !effectiveChannelId || !effectiveTeamsMessage.trim() || sendTeamsMutation.isPending}
                    onClick={() => sendTeamsMutation.mutate()}
                  >
                    {sendTeamsMutation.isPending ? 'Sending…' : 'Send to Teams'}
                  </button>
                </div>
              )}

              {sendTab === 'servicenow' && (
                <div className="field">
                  <button
                    className="btn btn--ghost btn--sm"
                    style={{ marginBottom: 'var(--sp-2)' }}
                    disabled={!sendTask.description?.trim() || draftTicketMutation.isPending}
                    onClick={() => draftTicketMutation.mutate()}
                  >
                    {draftTicketMutation.isPending ? 'Drafting…' : 'Draft ticket with AI'}
                  </button>
                  <label htmlFor="tasker-ticket-short">Short description</label>
                  <input
                    id="tasker-ticket-short"
                    type="text"
                    value={ticketShortDescription}
                    onChange={(e) => setTicketShortDescription(e.target.value)}
                  />
                  <label htmlFor="tasker-ticket-description">Ticket description</label>
                  <textarea
                    id="tasker-ticket-description"
                    rows={6}
                    value={ticketDescription}
                    onChange={(e) => setTicketDescription(e.target.value)}
                  />
                  <label htmlFor="tasker-ticket-urgency">Urgency</label>
                  <select id="tasker-ticket-urgency" value={ticketUrgency} onChange={(e) => setTicketUrgency(e.target.value)}>
                    <option value="1">High</option>
                    <option value="2">Medium</option>
                    <option value="3">Low</option>
                  </select>
                  {ticketError && <p style={{ color: 'var(--risk)' }}>{ticketError}</p>}
                  {ticketResult && (
                    <p style={{ color: 'var(--ok)' }}>
                      Created <a href={ticketResult.url} target="_blank" rel="noreferrer">{ticketResult.number}</a>.
                    </p>
                  )}
                  <button
                    className="btn btn--sm"
                    style={{ marginTop: 'var(--sp-2)' }}
                    disabled={!ticketShortDescription.trim() || createTicketMutation.isPending}
                    onClick={() => createTicketMutation.mutate()}
                  >
                    {createTicketMutation.isPending ? 'Creating…' : 'Create ticket'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
